/**
 * Block Type Chooser Component
 * Dropdown for selecting which type of content block to add.
 * Supports a "Questions" submenu and viewport-aware positioning.
 */

import { useEffect, useLayoutEffect, useRef, useState, useCallback } from 'react';
import { createPortal } from 'react-dom';
import {
  AUTHORING_BLOCK_TYPE_OPTIONS,
  QUESTION_BLOCK_TYPES,
  MAIN_MENU_BLOCK_TYPES,
  designTokens,
} from '@course-authoring/shared-types';
import type { BlockTypeOption } from '../utils/blockFactory';

export type { BlockTypeOption } from '../utils/blockFactory';

interface BlockTypeChooserProps {
  onSelect: (type: BlockTypeOption) => void;
  onClose: () => void;
  position?: { x: number; y: number };
  excludeBlockTypes?: string[];
}

const QUESTION_SUBMENU_LABEL = 'Questions';

export function BlockTypeChooser({ onSelect, onClose, position, excludeBlockTypes }: BlockTypeChooserProps) {
  const ref = useRef<HTMLDivElement>(null);
  const submenuRef = useRef<HTMLDivElement>(null);
  const questionsItemRef = useRef<HTMLButtonElement>(null);
  const [hoveredOption, setHoveredOption] = useState<string | null>(null);
  const [showQuestions, setShowQuestions] = useState(false);
  const [adjustedPosition, setAdjustedPosition] = useState<{ top?: number; bottom?: number } | null>(null);
  const [submenuPosition, setSubmenuPosition] = useState<{ top: number; left: number } | null>(null);
  const closeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const allOptions = AUTHORING_BLOCK_TYPE_OPTIONS;

  const mainMenuOptions = allOptions.filter(
    (opt) =>
      MAIN_MENU_BLOCK_TYPES.includes(opt.type) &&
      (!excludeBlockTypes || !excludeBlockTypes.includes(opt.type))
  );

  const questionOptions = allOptions.filter(
    (opt) =>
      QUESTION_BLOCK_TYPES.includes(opt.type) &&
      (!excludeBlockTypes || !excludeBlockTypes.includes(opt.type))
  );

  const showQuestionsSubmenu = questionOptions.length > 0;

  const lessonCompleteOption = allOptions.find((opt) => opt.type === 'lesson-complete');
  const showLessonComplete =
    lessonCompleteOption && (!excludeBlockTypes || !excludeBlockTypes.includes('lesson-complete'));

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (
        ref.current &&
        !ref.current.contains(e.target as Node) &&
        (!submenuRef.current || !submenuRef.current.contains(e.target as Node))
      ) {
        onClose();
      }
    };

    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEscape);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [onClose]);

  // Clamp menu within viewport after first render
  useLayoutEffect(() => {
    if (!ref.current) return;
    const rect = ref.current.getBoundingClientRect();
    const viewportHeight = window.innerHeight;
    const margin = 8;

    if (rect.top < margin) {
      setAdjustedPosition({ top: margin });
    } else if (rect.bottom > viewportHeight - margin) {
      setAdjustedPosition({ bottom: margin });
    }
  }, []);

  // Clamp the submenu within the viewport after it renders: when the trigger
  // sits near the bottom of the screen, shift the submenu up so it stays fully
  // visible (mirrors the main-menu clamp above). Also flip it to the left of
  // the trigger when there isn't enough room on the right.
  useLayoutEffect(() => {
    if (!showQuestions || !submenuPosition || !submenuRef.current || !ref.current) return;
    const rect = submenuRef.current.getBoundingClientRect();
    const containerRect = ref.current.getBoundingClientRect();
    const viewportHeight = window.innerHeight;
    const viewportWidth = window.innerWidth;
    const margin = 8;

    let top = submenuPosition.top;
    if (top + rect.height > viewportHeight - margin) {
      top = viewportHeight - margin - rect.height;
    }
    if (top < margin) {
      top = margin;
    }

    let left = submenuPosition.left;
    if (left + rect.width > viewportWidth - margin) {
      left = containerRect.left - rect.width - 4;
    }
    if (left < margin) {
      left = margin;
    }

    if (top !== submenuPosition.top || left !== submenuPosition.left) {
      setSubmenuPosition({ top, left });
    }
  }, [showQuestions, submenuPosition]);

  const cancelCloseTimer = useCallback(() => {
    if (closeTimerRef.current) {
      clearTimeout(closeTimerRef.current);
      closeTimerRef.current = null;
    }
  }, []);

  const scheduleClose = useCallback(() => {
    cancelCloseTimer();
    closeTimerRef.current = setTimeout(() => {
      setShowQuestions(false);
      setHoveredOption(null);
    }, 150);
  }, [cancelCloseTimer]);

  // Clean up timer on unmount
  useEffect(() => {
    return () => cancelCloseTimer();
  }, [cancelCloseTimer]);

  const handleQuestionsEnter = useCallback(() => {
    cancelCloseTimer();
    setShowQuestions(true);
    setHoveredOption(QUESTION_SUBMENU_LABEL);

    // Position the submenu using fixed coords from the trigger button
    if (questionsItemRef.current && ref.current) {
      const triggerRect = questionsItemRef.current.getBoundingClientRect();
      const containerRect = ref.current.getBoundingClientRect();
      setSubmenuPosition({
        top: triggerRect.top,
        left: containerRect.right + 4,
      });
    }
  }, [cancelCloseTimer]);

  const handleQuestionsLeave = useCallback(() => {
    scheduleClose();
  }, [scheduleClose]);

  const handleSubmenuEnter = useCallback(() => {
    cancelCloseTimer();
  }, [cancelCloseTimer]);

  const handleSubmenuLeave = useCallback(() => {
    scheduleClose();
  }, [scheduleClose]);

  // Build position style
  const positionStyle: React.CSSProperties = {};
  if (position) {
    positionStyle.position = 'fixed';
    positionStyle.left = position.x;
    positionStyle.top = position.y;
    positionStyle.maxHeight = `${window.innerHeight - position.y - 16}px`;
  }
  // Apply viewport clamping adjustments
  if (adjustedPosition) {
    if (adjustedPosition.top !== undefined) {
      positionStyle.top = adjustedPosition.top;
      positionStyle.bottom = 'auto';
    }
    if (adjustedPosition.bottom !== undefined) {
      positionStyle.bottom = adjustedPosition.bottom;
      positionStyle.top = 'auto';
    }
  }

  const submenuPortal =
    showQuestions && submenuPosition
      ? createPortal(
          <div
            ref={submenuRef}
            style={{
              ...styles.submenu,
              top: submenuPosition.top,
              left: submenuPosition.left,
            }}
            role="menu"
            aria-label="Question block types"
            onMouseEnter={handleSubmenuEnter}
            onMouseLeave={handleSubmenuLeave}
          >
            {questionOptions.map((option) => (
              <button
                key={option.type}
                type="button"
                style={{
                  ...styles.option,
                  ...(hoveredOption === option.type ? styles.optionHovered : {}),
                }}
                onClick={() => onSelect(option.type)}
                onMouseEnter={() => setHoveredOption(option.type)}
                onMouseLeave={() => setHoveredOption(QUESTION_SUBMENU_LABEL)}
                role="menuitem"
              >
                {option.chooserLabel}
              </button>
            ))}
          </div>,
          document.body
        )
      : null;

  return (
    <>
      <div ref={ref} style={{ ...styles.container, ...positionStyle }} role="menu" aria-label="Choose block type">
        <div style={styles.header}>BLOCK TYPE</div>
        {mainMenuOptions.map((option) => (
          <button
            key={option.type}
            type="button"
            style={{
              ...styles.option,
              ...(hoveredOption === option.type ? styles.optionHovered : {}),
            }}
            onClick={() => onSelect(option.type)}
            onMouseEnter={() => {
              setHoveredOption(option.type);
              setShowQuestions(false);
            }}
            onMouseLeave={() => setHoveredOption(null)}
            role="menuitem"
          >
            {option.chooserLabel}
          </button>
        ))}

        {showQuestionsSubmenu && (
          <button
            ref={questionsItemRef}
            type="button"
            style={{
              ...styles.option,
              ...styles.submenuTrigger,
              ...(hoveredOption === QUESTION_SUBMENU_LABEL ? styles.optionHovered : {}),
            }}
            onMouseEnter={handleQuestionsEnter}
            onMouseLeave={handleQuestionsLeave}
            onClick={() => setShowQuestions((prev) => !prev)}
            role="menuitem"
            aria-haspopup="true"
            aria-expanded={showQuestions}
          >
            {QUESTION_SUBMENU_LABEL}
            <span style={styles.submenuArrow} aria-hidden="true">›</span>
          </button>
        )}

        {showLessonComplete && (
          <button
            type="button"
            style={{
              ...styles.option,
              ...(hoveredOption === 'lesson-complete' ? styles.optionHovered : {}),
            }}
            onClick={() => onSelect('lesson-complete')}
            onMouseEnter={() => {
              setHoveredOption('lesson-complete');
              setShowQuestions(false);
            }}
            onMouseLeave={() => setHoveredOption(null)}
            role="menuitem"
          >
            {lessonCompleteOption!.chooserLabel}
          </button>
        )}
      </div>
      {submenuPortal}
    </>
  );
}

const styles: Record<string, React.CSSProperties> = {
  container: {
    backgroundColor: '#fff',
    borderRadius: '14px',
    boxShadow: '0 10px 30px rgba(0, 0, 0, 0.12)',
    border: `1px solid ${designTokens.colors.blockHoverBackground}`,
    padding: '16px',
    minWidth: '280px',
    maxHeight: 'calc(100vh - 32px)',
    overflowY: 'auto',
    zIndex: 100,
  },
  header: {
    marginBottom: '12px',
    padding: '0 10px',
    fontSize: '12px',
    fontWeight: 700,
    letterSpacing: '0.08em',
    color: designTokens.colors.textMuted,
  },
  option: {
    display: 'block',
    width: '100%',
    padding: '8px 10px',
    backgroundColor: 'transparent',
    borderWidth: 0,
    borderStyle: 'none',
    borderColor: 'transparent',
    borderRadius: '10px',
    cursor: 'pointer',
    textAlign: 'left',
    color: designTokens.colors.textPrimary,
    fontSize: '14px',
    fontWeight: 400,
    lineHeight: 1.35,
    transition: 'background-color 150ms ease',
  },
  optionHovered: {
    backgroundColor: designTokens.colors.blockHoverBackground,
  },
  submenuTrigger: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  submenuArrow: {
    fontSize: '18px',
    lineHeight: 1,
    color: designTokens.colors.textMuted,
  },
  submenu: {
    position: 'fixed' as const,
    backgroundColor: '#fff',
    borderRadius: '14px',
    boxShadow: '0 10px 30px rgba(0, 0, 0, 0.12)',
    border: `1px solid ${designTokens.colors.blockHoverBackground}`,
    padding: '12px',
    paddingLeft: '16px',
    minWidth: '220px',
    zIndex: 101,
  },
};
